import { useState } from 'react';
import { Button, Checkbox, Input, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { RequestBodyField } from '@core/entities/ApiRequest';
import { FilePicker, type FilePickerValue } from '@presentation/components/workbench/FilePicker';
import { container } from '@infrastructure/di/container';

interface MultipartFieldRowsProps {
  rows: RequestBodyField[];
  onChange: (rows: RequestBodyField[]) => void;
  createRow: () => RequestBodyField;
}

/**
 * Multipart form fields, text or file. Each row is an independent part —
 * "multiple files" for the same logical field is achieved by adding
 * several file-type rows sharing a key (e.g. `photos[]`), the same way a
 * plain HTML multipart form would send an array field, rather than a
 * single row holding more than one file.
 */
export const MultipartFieldRows = ({ rows, onChange, createRow }: MultipartFieldRowsProps) => {
  // Same session-local metadata cache as BodyEditor's binary picker — the
  // field only persists the upload id (`filePath`), not filename/size/mime.
  const [fileMeta, setFileMeta] = useState<Record<number, FilePickerValue>>({});

  const updateRow = (index: number, patch: Partial<RequestBodyField>) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index: number) => {
    const removed = rows[index];
    if (removed?.type === 'file' && removed.filePath) {
      container.uploads.remove(removed.filePath).catch(() => {});
    }
    onChange(rows.filter((_, i) => i !== index));
    setFileMeta((current) => {
      const next = { ...current };
      delete next[index];
      return next;
    });
  };

  const addRow = () => onChange([...rows, createRow()]);

  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((row, index) => {
        const isLast = index === rows.length - 1;
        return (
          <div key={index} className="flex items-center gap-2">
            <Checkbox
              checked={row.isEnabled}
              onChange={(event) => updateRow(index, { isEnabled: event.target.checked })}
            />
            <Select
              size="small"
              className="w-20 shrink-0"
              options={[
                { label: 'Text', value: 'text' },
                { label: 'File', value: 'file' },
              ]}
              value={row.type}
              onChange={(value: 'text' | 'file') =>
                updateRow(index, { type: value, value: null, filePath: null })
              }
            />
            <Input
              size="small"
              placeholder="Key"
              value={row.key}
              onChange={(event) => updateRow(index, { key: event.target.value })}
              className="!w-40 shrink-0"
            />
            {row.type === 'file' ? (
              <FilePicker
                label={row.key || 'file'}
                value={
                  row.filePath
                    ? fileMeta[index]?.uploadId === row.filePath
                      ? fileMeta[index]
                      : {
                          uploadId: row.filePath,
                          filename: 'Previously uploaded file',
                          mimeType: null,
                          size: 0,
                        }
                    : null
                }
                onChange={(value) => {
                  setFileMeta((current) => (value ? { ...current, [index]: value } : current));
                  updateRow(index, { filePath: value?.uploadId ?? null });
                }}
              />
            ) : (
              <Input
                size="small"
                placeholder="Value"
                value={row.value ?? ''}
                onChange={(event) => updateRow(index, { value: event.target.value })}
                className="flex-1"
              />
            )}
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeRow(index)}
              aria-label="Remove row"
            />
            {isLast && (
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={addRow}
                aria-label="Add row"
              />
            )}
          </div>
        );
      })}

      {rows.length === 0 && (
        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={addRow}
          className="self-start"
          aria-label="Add row"
        />
      )}
    </div>
  );
};
